import React from 'react';
import { Loader2, ChevronRight } from 'lucide-react';
import type { ActivityPart } from '../../types/chat';

interface ActivityIndicatorProps {
  part: ActivityPart;
}

/**
 * Inline component for transient activity events.
 *
 * Shows agent name (with ancestry breadcrumb for nested chains),
 * activity message, and a spinner for in-progress states.
 * Renders nothing for agent-complete (those use TokenUsageSummaryCard).
 */
export function ActivityIndicator({ part }: ActivityIndicatorProps) {
  const { data } = part;

  // agent-complete events are rendered by TokenUsageSummaryCard
  if (data.activityType === 'agent-complete') return null;

  const isError = data.activityType === 'agent-error';
  const depth = data.ancestry.length;
  const nestingMargin = depth > 1 ? `${(depth - 1) * 12}px` : '0px';

  // Build attribution: use detail for skill/MCP attribution (T027)
  const detail = data.detail as Record<string, unknown> | undefined;
  let attribution = data.source;
  if (detail?.skill_name) {
    attribution = `${data.source} > ${detail.skill_name as string}`;
  } else if (detail?.server_name) {
    attribution = `${data.source} > ${detail.server_name as string}`;
  }

  return (
    <div
      className={`flex items-center gap-2 py-1 px-2 text-xs rounded-lg ${
        isError
          ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800'
          : 'text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800'
      }`}
      style={{ marginLeft: nestingMargin }}
      data-testid="activity-indicator"
      data-source={data.source}
    >
      {!isError && (
        <Loader2 className="w-3 h-3 animate-spin text-emerald-500 flex-shrink-0" />
      )}
      {/* Ancestry breadcrumb for nested chains */}
      {depth > 1 && (
        <span className="flex items-center gap-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0">
          {data.ancestry.slice(0, -1).map((agent, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight className="w-2.5 h-2.5" />}
              <span>{agent}</span>
            </React.Fragment>
          ))}
          <ChevronRight className="w-2.5 h-2.5" />
        </span>
      )}
      <span className="font-medium">{attribution}</span>
      <span className="text-gray-400 dark:text-gray-500">:</span>
      <span className="truncate">{data.message}</span>
    </div>
  );
}
